import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const ALWAYS_ALLOWED = new Set([
  "/login",
  "/admin/login",
  "/register",
  "/verify-otp",
  "/verify-email",
  "/awaiting-approval",
  "/contact-us",
  "/privacy-policy",
  "/terms-of-service",
  "/ai-hackathon-terms",
]);

function isAlwaysAllowed(pathname: string) {
  if (ALWAYS_ALLOWED.has(pathname)) return true;
  return pathname.startsWith("/admin/login");
}

/**
 * Keeps non-admin users in the correct onboarding state:
 * email OTP → awaiting admin approval → full portal access after approval.
 */
export default function AccountStatusGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || !user || user.role === "admin") return;

    const path = location.pathname;
    if (isAlwaysAllowed(path)) return;

    if (!user.isEmailVerified) {
      navigate("/verify-otp", { replace: true, state: { email: user.email } });
      return;
    }

    if (!user.isApproved) {
      navigate("/awaiting-approval", { replace: true });
    }
  }, [user, isLoading, location.pathname, navigate]);

  return <>{children}</>;
}
